/** Visual tokens aligned with metricsReportWeb/reportWebTheme.ts */

export const PDF_FONT_FAMILY = "DM Sans";

export type PdfColorMode = "light" | "dark";

/** Same muted gray as Programas en YouTube metric labels (VIEWS / PROM.). */
const MUTED_GRAY_LIGHT = "#6B7280";
const MUTED_GRAY_DARK = "#9CA3AF";

export type PdfTheme = {
  mode: PdfColorMode;
  pageBg: string;
  cardBg: string;
  brandPrimary: string;
  textBody: string;
  textMuted: string;
  textMutedAlt: string;
  labelMuted: string;
  borderLight: string;
  /** @react-pdf does not support rgba() — use hex or borders render red */
  borderDivider: string;
  cardBorder: string;
  headerBg: string;
  rowDivider: string;
  chartFill: string;
  chartLine: string;
  chartPeak: string;
  deltaUp: string;
  deltaDown: string;
  barTrack: string;
  /** Text on filled rank/avatar badges — stays light on both modes. */
  textOnAccent: string;
};

export const PDF_THEME_LIGHT: PdfTheme = {
  mode: "light",
  pageBg: "#FFFFFF",
  cardBg: "#FFFFFF",
  brandPrimary: "#131116",
  textBody: "#1A1A1A",
  textMuted: MUTED_GRAY_LIGHT,
  textMutedAlt: MUTED_GRAY_LIGHT,
  labelMuted: MUTED_GRAY_LIGHT,
  borderLight: "#E0E0E0",
  borderDivider: "#E8E8EA",
  cardBorder: "#E8E8EA",
  headerBg: "#F5F5F5",
  rowDivider: "#F2F2F2",
  chartFill: "#F9FAFB",
  chartLine: "#131116",
  chartPeak: "#9CA3AF",
  deltaUp: "#4C9A93",
  deltaDown: "#B85A52",
  barTrack: "#F1F1F2",
  textOnAccent: "#FFFFFF",
};

export const PDF_THEME_DARK: PdfTheme = {
  mode: "dark",
  pageBg: "#131116",
  cardBg: "#1C1B20",
  brandPrimary: "#F5F5F5",
  textBody: "#F0F0F2",
  textMuted: MUTED_GRAY_DARK,
  textMutedAlt: MUTED_GRAY_DARK,
  labelMuted: "#8B919A",
  borderLight: "#3A3840",
  borderDivider: "#1F1E24",
  cardBorder: "#2E2C33",
  headerBg: "#242228",
  rowDivider: "#2A282E",
  chartFill: "#1A191E",
  chartLine: "#F5F5F5",
  chartPeak: "#6B7280",
  deltaUp: "#5BB5AD",
  deltaDown: "#D07068",
  barTrack: "#2E2C33",
  textOnAccent: "#FFFFFF",
};

export function getPdfTheme(mode: PdfColorMode = "light"): PdfTheme {
  return mode === "dark" ? PDF_THEME_DARK : PDF_THEME_LIGHT;
}

/** Default light theme (backward-compatible import name). Prefer `usePdfTheme()`. */
export const PDF_THEME = PDF_THEME_LIGHT;

export const PDF_CARD_RADIUS = 8;
export const PDF_CARD_BORDER_WIDTH = 1;

/** fontWeight 500/600 in web design → normal/bold with DM Sans Regular/Bold only */
export const PDF_FONT = {
  medium: "normal" as const,
  semibold: "bold" as const,
  bold: "bold" as const,
};
